import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../hooks/useWishlist';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import { TrashIcon, ShoppingCartIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { usePageScroll } from '../hooks/usePageScroll';

export const Wishlist: React.FC = () => {
    usePageScroll('wishlist');

    const navigate = useNavigate();
    const { wishlistItems, loading, removeFromWishlist, refreshWishlist } = useWishlist();
    const { addToCart } = useCart();
    const [addingToCart, setAddingToCart] = useState<number | null>(null);
    const [userId, setUserId] = useState<string | null>(null);

    // Проверяем авторизацию
    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) {
                // Не авторизован, но не редиректим сразу, покажем сообщение
                setUserId(null);
            } else {
                setUserId(session.user.id);
            }
        };
        checkAuth();
    }, []);

    const handleAddToCart = async (item: typeof wishlistItems[0]) => {
        if (item.quantity === 0) {
            toast.error('Товар отсутствует на складе');
            return;
        }

        setAddingToCart(item.variant_id);
        try {
            await addToCart(item.product_id, item.variant_id, 1);
        } catch (error: any) {
            console.error('Error adding to cart:', error);
            if (error?.message?.includes('stock') || error?.message?.includes('quantity')) {
                toast.error('Недостаточно товара на складе');
            } else {
                toast.error('Ошибка добавления в корзину');
            }
        } finally {
            setAddingToCart(null);
        }
    };

    const handleRemove = async (variantId: number) => {
        await removeFromWishlist(variantId);
    };

    const handleProductClick = (productId: number, variantId: number) => {
        navigate(`/product/${productId}`, {
            state: { selectedVariantId: variantId }
        });
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner"></div>
                <p>Загрузка избранного...</p>
            </div>
        );
    }

    // Если пользователь не авторизован
    if (!userId) {
        return (
            <div className="empty-state">
                <div className="empty-icon">❤️</div>
                <h3>Войдите в аккаунт</h3>
                <p>Чтобы просмотреть избранное, войдите в аккаунт</p>
                <button onClick={() => navigate('/login')} className="auth-button">
                    Войти
                </button>
            </div>
        );
    }

    return (
        <div className="wishlist-page">
            <div className="wishlist-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <ArrowLeftIcon style={{ width: 20, height: 20 }} />
                </button>
                <h1>Избранное</h1>
                <div className="placeholder" />
            </div>

            {wishlistItems.length === 0 ? (
                <div className="empty-wishlist">
                    <div className="empty-icon">❤️</div>
                    <h3>Избранное пусто</h3>
                    <p>Добавляйте товары в избранное, чтобы не потерять их</p>
                    <button onClick={() => navigate('/products')} className="auth-button">
                        Перейти в каталог
                    </button>
                </div>
            ) : (
                <div className="wishlist-grid">
                    {wishlistItems.map((item) => (
                        <div
                            key={item.id}
                            className="wishlist-card"
                            onClick={() => handleProductClick(item.product_id, item.variant_id)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="wishlist-card-image">
                                <img src={item.product_image} alt={item.product_name} />
                                {item.quantity === 0 && (
                                    <div className="stock-badge out-of-stock">Нет в наличии</div>
                                )}
                            </div>

                            <div className="wishlist-card-info">
                                <button
                                    className="wishlist-remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(item.variant_id);
                                    }}
                                >
                                    <TrashIcon style={{ width: 18, height: 18 }} />
                                </button>

                                <h3 className="wishlist-card-title">
                                    {item.product_name}
                                </h3>

                                {item.variant_name && (
                                    <p className="wishlist-card-variant">{item.variant_name}</p>
                                )}

                                <div className="wishlist-card-footer">
                                    <span className="wishlist-card-price">
                                        {item.price.toLocaleString()} TJS
                                    </span>
                                </div>
                                <div className="wishlist-card-footer">
                                    {item.quantity > 0 && item.quantity < 5 && (
                                        <span className="wishlist-low-stock">Осталось {item.quantity} шт.</span>
                                    )}

                                    <button
                                        className={`wishlist-cart-btn ${(!item.quantity || item.quantity === 0) ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddToCart(item);
                                        }}
                                        disabled={addingToCart === item.variant_id || !item.quantity || item.quantity === 0}
                                    >
                                        <ShoppingCartIcon style={{ width: 18, height: 18 }} />
                                        <span className="wishlist-cart-btn-title">
                                            {addingToCart === item.variant_id ? '...' : 'В корзину'}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};